'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import {
  buySubscription,
  type Subscription,
} from '@eliadmin/core/src/api/subscription/subscription-api';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { SubscriptionCard } from './subscription-card';
import Link from '@eliadmin/core/src/components/link';
import { SignInForm } from '../auth/sign-in-form';

export function BuySubscriptionForm({
  subscriptions,
}: {
  subscriptions: Subscription[];
}) {
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);
  const [isCompany, setIsCompany] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    address: '',
    street_number: '',
    city: '',
    postcode: '',
    country: { id: 'SK' },
    organization_name: '',
    role_in_organization: '',
    company: '',
    ico: '',
    dic: '',
    ic_dph: '',
    coupon: '',
    gdpr: false,
  });

  useEffect(() => {
    if (me?.customer) {
      setFormData({
        name: me.customer.name || '',
        surname: me.customer.surname || '',
        email: me.customer.email || '',
        phone: me.customer.phone || '',
        address: me.customer.address || '',
        street_number: me.customer.street_number || '',
        city: me.customer.city || '',
        postcode: me.customer.postcode || '',
        country: { id: me.customer.country?.id || 'SK' },
        organization_name: me.customer.organization_name || '',
        role_in_organization: me.customer.role_in_organization || '',
        company: me.customer.company || '',
        ico: me.customer.ico || '',
        dic: me.customer.dic || '',
        ic_dph: me.customer.ic_dph || '',
        coupon: '',
        gdpr: false,
      });
    }
  }, [me]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleConfirm = (e: React.FormEvent, subscriptionId: string) => {
    e.preventDefault();
    if (!me?.authToken || !formData.gdpr) return;

    const requestBody = {
      ...formData,
      company_buy: isCompany,
      order_subscription_type: 'one-time-subscription',
      return_url:
        `${process.env.NEXT_PUBLIC_APP_URL}${ROUTES.ORDER_CONFIRM()}` ||
        'https://new.danceana.sk/potvrdenie-objednavky',
    };

    buySubscription({
      authToken: me.authToken,
      body: requestBody as any,
      subscriptionId,
    })
      .then(res => {
        if (res?.redirect?.redirect) {
          window.location.href = res.redirect.redirect;
        }
      })
      .catch(error => {
        console.error('Error in buySubscription:', error);
        console.log('Error response:', error.response);
      });
  };

  if (!me?.customer) return <SignInPrompt />;

  return (
    <form
      onSubmit={e => handleConfirm(e, '1')}
      className="col-span-12 grid w-full grid-cols-12 gap-x-5 gap-y-6"
    >
      <SubscriptionFormContainer title={t('text360')}>
        <Input
          name="email"
          type="email"
          required
          placeholder={t('text2238')}
          value={formData.email}
          onChange={handleInputChange}
          className={{ wrapper: 'col-span-12 w-full' }}
        />
        <Input
          name="phone"
          type="tel"
          required
          value={formData.phone}
          placeholder={t('text2239')}
          onChange={handleInputChange}
          className={{ wrapper: 'col-span-12 w-full' }}
        />
      </SubscriptionFormContainer>
      <SubscriptionFormContainer title={t('text679')}>
        <div className="col-span-12 flex items-center justify-start gap-2">
          <Checkbox
            id="company_buy"
            checked={isCompany}
            onCheckedChange={() => setIsCompany(!isCompany)}
          />
          <Label htmlFor="company_buy" className="text-sm font-normal">
            {t('text2235')}
          </Label>
        </div>
        <Input
          name="name"
          type="text"
          required
          value={formData.name}
          onChange={handleInputChange}
          placeholder={t('text2240')}
          className={{ wrapper: 'col-span-12 w-full' }}
        />
        <Input
          name="surname"
          type="text"
          required
          value={formData.surname}
          onChange={handleInputChange}
          placeholder={t('text2241')}
          className={{ wrapper: 'col-span-12 w-full' }}
        />
        <div className="col-span-12 w-full sm:col-span-8">
          <Input
            name="address"
            type="text"
            required
            value={formData.address}
            onChange={handleInputChange}
            placeholder={t('text38')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
        </div>
        <div className="col-span-12 w-full sm:col-span-4">
          <Input
            name="street_number"
            type="text"
            required
            value={formData.street_number}
            onChange={handleInputChange}
            placeholder={t('text438')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
        </div>
        <div className="col-span-12 w-full sm:col-span-4">
          <Input
            name="postcode"
            type="text"
            required
            value={formData.postcode}
            onChange={handleInputChange}
            placeholder={t('text40')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
        </div>
        <div className="col-span-12 w-full sm:col-span-8">
          <Input
            name="city"
            type="text"
            required
            value={formData.city}
            onChange={handleInputChange}
            placeholder={t('text39')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
        </div>
        {isCompany && (
          <>
            <Input
              name="company"
              type="text"
              required
              value={formData.company}
              onChange={handleInputChange}
              placeholder={t('text328')}
              className={{ wrapper: 'col-span-12 w-full' }}
            />
            <Input
              name="ico"
              type="text"
              required
              value={formData.ico}
              onChange={handleInputChange}
              placeholder={t('text42')}
              className={{ wrapper: 'col-span-12 w-full' }}
            />
            <Input
              name="dic"
              type="text"
              required
              value={formData.dic}
              onChange={handleInputChange}
              placeholder={t('text43')}
              className={{ wrapper: 'col-span-12 w-full' }}
            />
          </>
        )}
        <div className="col-span-12 flex items-center">
          <Checkbox
            id="gdpr"
            name="gdpr"
            checked={formData.gdpr}
            onCheckedChange={checked => setFormData(prev => ({ ...prev, gdpr: !!checked }))}
            required
          />
          <Label htmlFor="gdpr" className="ml-2 text-sm">
            {t('text363')}
          </Label>
        </div>
      </SubscriptionFormContainer>
      <SubscriptionFormContainer title={t('text1917')}>
        {/* <p className="col-span-12 text-sm font-normal tracking-tighter text-black/70 sm:text-base">
          {t('text2237')}
        </p> */}
        <SubscriptionCard
          className={{ wrapper: 'col-span-12 w-full', footer: 'hidden' }}
          subscription={subscriptions[0]}
        />
      </SubscriptionFormContainer>
      <div className="col-span-12 mt-4 flex gap-4">
        <Button type="submit" disabled={!formData.gdpr}>
          {t('text2043')}
        </Button>
      </div>
    </form>
  );
}

function SubscriptionFormContainer({
  title,
  className,
  children,
}: {
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'col-span-12 grid w-full grid-cols-12 gap-x-5 gap-y-4 sm:gap-y-6',
        'ea-cart-order-form__container',
        className,
      )}
    >
      {title && (
        <h4 className="col-span-12 font-heading text-xl font-normal tracking-tighter sm:text-2xl lg:text-3xl">
          {title}
        </h4>
      )}
      {children}
    </div>
  );
}

function SignInPrompt() {
  const { t } = useTranslations();

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-6">
      <div className="flex w-full flex-col items-center justify-center gap-2">
        <h3
          className={cn(
            'text-center font-heading text-3xl font-medium tracking-tighter sm:text-4xl lg:text-5xl',
            'ea-sign-in-form__header-title',
          )}
        >
          {t('text190')}
        </h3>
        <p className="w-full text-center text-sm font-normal text-black/70 sm:text-base">
          {t('text2243')}
          <br />
          {t('text2244')}{' '}
          <Link prefetch={false} href={ROUTES.REGISTER()} className="font-medium text-primary">
            {t('text2245')}
          </Link>
        </p>
      </div>
      <SignInForm hideHeader />
    </div>
  );
}
